import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, OnInit, Signal, viewChild } from '@angular/core';
import { features } from '@consts/text.const';
import { WorkflowModel } from '@models/workflow.model';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SharedModels } from '@share-models';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { WorkflowsSelectors } from '@states/workflow/workflow.selector-types';
import { PreloaderColor, UiNoDataComponent, UiPreloaderComponent } from '@ui';
import { Chart, ChartType, registerables } from 'chart.js';
import moment from 'moment-timezone';
import 'chartjs-adapter-moment';

const barColors = [
  "#748CED", // Primary-400
  "#F87171", // Red-400
  "#FB923C", // Orange-400
  "#FACC15", // Yellow-400
  "#A3E635", // Lime-400
  "#4ADE80", // Green-400
  "#2DD4BF", // Teal-400
  "#22D3EE", // Cyan-400
  "#60A5FA", // Blue-400
  "#818CF8", // Indigo-400
  "#A78BFA", // Violet-400
  "#C084FC", // Purple-400
  "#E879F9", // Fuchsia-400
  "#F472B6", // Pink-400
  "#D0D5DD", // Gray-400
];

const tooltipOptions = {
  displayColors: false,
  backgroundColor: '#ffffff',
  titleColor: '#000000',
  bodyColor: '#000000',
  borderColor: '#cccccc',
  borderWidth: 1,
}

interface ChartStepData {
  durations: number[];
  labels: string[];
  startDates: number[];
  endDates: number[];
}

type ChartStepDataKeys = keyof ChartStepData;

@Component({
  selector: 'run-steps-chart',
  imports: [
    UiPreloaderComponent,
    UiNoDataComponent,
    TranslatePipe,
  ],
  templateUrl: './run-steps-chart.component.html',
  styleUrl: './run-steps-chart.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RunStepsChartComponent implements OnInit {
  private readonly store$ = inject(Store);
  private readonly translateService = inject(TranslateService);

  public graphId = input.required<string>();
  public orgId = input.required<string>();
  public runId = input.required<string>();
  public chartConfig = input<{
    type: ChartType,
    dataParseKey: ChartStepDataKeys,
  }>({
    type: 'bar',
    dataParseKey: 'durations',
  });

  public canvas = viewChild<ElementRef<HTMLCanvasElement>>('chartCanvas');

  public chart: Chart;

  public readonly preloaderColor = PreloaderColor;
  protected readonly noData = features.tags.noResults;
  protected readonly viewStates = SharedModels.ViewState;

  public selectIsStepsLoadingSig: Signal<boolean> =
    this.store$.selectSignal(WorkflowsSelectors.selectIsStepsLoading);
  public selectStepsSig: Signal<WorkflowModel.GetRunStepsResponse> =
    this.store$.selectSignal(WorkflowsSelectors.selectSteps);

  protected readonly viewStateSig = computed(() => {
    const steps = this.selectStepsSig();

    if (steps?.stepsById) {
      return SharedModels.ViewState.HAS_CONTENT;
    }

    return SharedModels.ViewState.INITIAL_EMPTY;
  });

  private chartStepDataSig: Signal<ChartStepData | null> = computed(() => {
    const steps = this.selectStepsSig();

    if (!Object.keys(steps?.stepsById ?? {}).length) {
      return null;
    }

    return Object.values(steps?.stepsById).reduce((acc: ChartStepData, step) => {

      //temp fix, remove when all nodes will be on BE
      const nodeType = WorkflowModel.NodeTypesMap[step.nodeType] ? this.translateService.instant(WorkflowModel.NodeTypesMap[step.nodeType]) : step.nodeType;
      const duration = step.metrics.durationMs;
      const startDate = step.metrics.startedAt;
      const endDate = step.metrics.endedAt;

      acc.durations = [duration, ...acc.durations];
      acc.labels = [nodeType, ...acc.labels];
      acc.startDates = [startDate, ...acc.startDates];
      acc.endDates = [endDate, ...acc.endDates];

      return acc;
    }, {
      durations: [],
      labels: [],
      startDates: [],
      endDates: []
    });
  })

  constructor() {
    effect(() => {
      const chartStepData = this.chartStepDataSig();

      if (chartStepData) {
        setTimeout(() => {
          this.createChart(chartStepData);
        }, 0);
      }
    });
  }

  public ngOnInit(): void {
    Chart.register(...registerables);

    const steps = this.selectStepsSig();

    if (!Object.keys(steps?.stepsById ?? {}).length) {
      this.store$.dispatch(WorkflowActions.getWorkflowRunSteps({
        orgId: this.orgId(),
        graphId: this.graphId(),
        runId: this.runId(),
        view: 'full',
      }))
    }
  }

  private createChart(stepData: ChartStepData) {
    if (!!this.chart) {
      this.chart.destroy();
    }

    const gridColor = getComputedStyle(document.body)
      .getPropertyValue('--color-border-button-default-hover')
      .trim();

    const labelsColor = getComputedStyle(document.body).getPropertyValue('--color-text-subtle').trim();

    const ctx = this.canvas()?.nativeElement.getContext('2d');

    const data = {
      labels: stepData.labels,
      datasets: [{
        data: stepData[this.chartConfig().dataParseKey] as number[],
        backgroundColor: stepData.labels.map((_, index) => {
          return barColors[index % barColors.length];
        }),
        borderRadius: 2,
      }]
    };

    const yScaleConfig = this.getYScaleConfig({
      dataParseKey: this.chartConfig().dataParseKey,
      labelsColor: labelsColor,
      startDates: stepData.startDates
    });

    const formatDateFn = this.formatDate.bind(this);

    const startDateLabel = this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.START_DATE');
    const endDateLabel = this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.END_DATE');
    const durationLabel = this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.DURATION');
    const secondsLabel = this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.SECONDS');

    const config = {
      type: this.chartConfig().type,
      data: data,
      options: {
        plugins: {
          tooltip: {
            ...tooltipOptions,
            callbacks: {
              title: function (context) {
                return context[0].label;
              },
              label: function (context) {
                const index = context.dataIndex;

                return [
                  `${durationLabel}: ${(stepData.durations[index] / 1000).toFixed(2)} ${secondsLabel}`,
                  `${startDateLabel}: ${formatDateFn(stepData.startDates[index])}`,
                  `${endDateLabel}: ${formatDateFn(stepData.endDates[index])}`
                ];
              }
            }
          },
          legend: {
            display: false,
          }
        },
        scales: {
          y: {
            ...yScaleConfig,
            title: {
              display: true,
              text: this.getYLabel(this.chartConfig().dataParseKey),
              color: labelsColor,
            },
            grid: {
              color: gridColor,
            },
          },
          x: {
            title: {
              display: true,
              text: this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.NODES'),
              color: labelsColor,
            },
            grid: {
              color: gridColor,
            },
            ticks: {
              color: labelsColor,
            },
          }
        }
      }
    };

    this.chart = new Chart(ctx, { ...config });
  }

  private getYScaleConfig(
    {
      dataParseKey,
      labelsColor,
      startDates,
    }:
      {
        dataParseKey: keyof ChartStepData,
        labelsColor: string,
        startDates: number[],
      }) {
    switch (dataParseKey) {
      case 'durations':
        return {
          ticks: {
            color: labelsColor,
            callback: function (value) {
              return (value / 1000).toFixed(0);
            }
          }
        }

      case 'startDates':
        return {
          type: 'time' as any,
          min: Math.min(...startDates) - 500,
          time: {
            unit: 'second',
            displayFormats: {
              second: 'HH:mm:ss',
              minute: 'HH:mm',
              hour: 'HH:mm',
              day: 'DD MMM YYYY',
            },
            tooltipFormat: 'HH:mm:ss',
          }
        }

      default:
        return {}
    }
  }

  private getYLabel(dataParseKey: keyof ChartStepData) {
    switch (dataParseKey) {
      case 'durations':
        return this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.DURATION_SEC');

      case 'startDates':
        return this.translateService.instant('FLOW_CHART.RUN_STEPS_VISUALISATION.START_DATE');

      default:
        return '';
    }
  }

  private formatDate(timestamp: number): string {
    const currentLang = this.translateService.getCurrentLang();

    if (currentLang) { try { moment.locale(currentLang); } catch { } }

    const mdyBase = 'YY MMM DD, HH:mm:ss z';
    const m = moment(timestamp);
    const mtz = m.tz(moment.tz.guess());

    return mtz.format(`${mdyBase}`)
  }
}
